import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Loader2, CheckCircle, Mail, Handshake, Newspaper, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { value: "media", label: "Media Inquiry", icon: Newspaper },
  { value: "collaboration", label: "Collaboration", icon: Handshake },
  { value: "partnership", label: "Partnership", icon: Briefcase },
  { value: "consulting", label: "Consulting", icon: Mail },
  { value: "other", label: "Other", icon: Mail },
];

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiry_type: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === "success") {
        setIsSuccess(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      id="contact"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 relative"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-96 h-96 gradient-blob" />
      
      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
              Get in Touch
            </span>
            <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
              Let's <span className="gradient-text">Connect</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed">
              Whether you have a media inquiry, collaboration idea, or business opportunity, 
              I'd love to hear from you. Fill out the form and my team will respond promptly.
            </p>

            {/* Inquiry Types */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {inquiryTypes.slice(0, 4).map((type, index) => (
                <motion.div
                  key={type.value}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="neon-card p-4"
                >
                  <type.icon className="w-5 h-5 text-primary mb-2" />
                  <span className="text-sm font-semibold uppercase tracking-wider text-foreground">{type.label}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground">
              For speaking inquiries, please use the{" "}
              <a href="#speaking-form" className="text-primary hover:underline">
                speaking inquiry form
              </a>{" "}
              above.
            </p>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {isSuccess ? (
              <div className="neon-card p-12 text-center h-full flex flex-col items-center justify-center">
                <CheckCircle className="text-primary mb-4" size={48} />
                <h3 className="font-['Orbitron'] text-2xl font-bold uppercase text-foreground mb-2">
                  Message Sent!
                </h3>
                <p className="text-muted-foreground">
                  Thank you for reaching out. We'll get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="neon-card p-6 lg:p-8 space-y-6">
                <div>
                  <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                    Name <span className="text-pink-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-background border-primary/30 focus:border-primary rounded-lg h-12 text-foreground placeholder:text-muted-foreground"
                    placeholder="Your full name"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                    Email <span className="text-pink-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-background border-primary/30 focus:border-primary rounded-lg h-12 text-foreground placeholder:text-muted-foreground"
                    placeholder="your@email.com"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                    Inquiry Type <span className="text-pink-500">*</span>
                  </label>
                  <Select
                    value={formData.inquiry_type}
                    onValueChange={(value) => setFormData({ ...formData, inquiry_type: value })}
                  >
                    <SelectTrigger 
                      className="bg-background border-primary/30 focus:border-primary rounded-lg h-12 text-foreground"
                      data-testid="contact-form-type"
                    >
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border-primary/30">
                      {inquiryTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-foreground hover:bg-primary/10">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                    Message <span className="text-pink-500">*</span>
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-background border-primary/30 focus:border-primary rounded-lg min-h-[150px] text-foreground placeholder:text-muted-foreground"
                    placeholder="Tell us about your inquiry..."
                    data-testid="contact-form-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary text-primary-foreground rounded-full h-12 font-bold uppercase tracking-wider hover:bg-primary/90 transition-all btn-glow"
                  data-testid="contact-form-submit-btn"
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
